import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #swapToken = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // BMP-HOOK-START:perf-seamless-swap
    // Keep the current image on screen until the new variant's first image is decoded,
    // instead of swapping in a gallery whose image still has to load and decode (which
    // flashed an empty frame). A newer selection cancels a swap that is still waiting.
    const swapToken = ++this.#swapToken;
    // BMP-HOOK-END:perf-seamless-swap

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        await whenFirstImageReady(newMediaGallery);
        if (swapToken !== this.#swapToken || !this.isConnected) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Resolves once the gallery's first image is downloaded and decoded, or after a short
 * timeout so a slow connection never blocks the swap for long. Uses a detached Image
 * with the same srcset/sizes, so the gallery <img> then paints from the browser cache.
 * BMP-HOOK:perf-seamless-swap
 *
 * @param {Element} gallery - The incoming media gallery.
 * @returns {Promise<void>}
 */
function whenFirstImageReady(gallery) {
  const source = gallery.querySelector('.product-media__image');
  if (!(source instanceof HTMLImageElement)) return Promise.resolve();

  // Paint the swapped-in image together with the rest of the gallery, not a frame later.
  source.decoding = 'sync';

  const image = new Image();
  // sizes must be set before srcset so the browser picks the same candidate.
  image.sizes = source.getAttribute('sizes') ?? '';
  image.srcset = source.getAttribute('srcset') ?? '';
  image.src = source.getAttribute('src') ?? '';

  const decoded = image.decode().catch(() => {});
  // Short cap: if the photo isn't ready by then, the new gallery shows its blurred
  // placeholder (preloaded by bmp-variant-preload.js) rather than the old colour.
  const timeout = new Promise((resolve) => setTimeout(resolve, 300));

  return Promise.race([decoded, timeout]).then(() => {});
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
